import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';

export interface CartItem {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  quantity: number;
}

interface CartContextData {
  cart: CartItem[];
  loading: boolean;
  addItem: (product: Omit<CartItem, 'quantity'>) => Promise<void>;
  removeItem: (productId: string) => Promise<void>;
  updateQuantity: (productId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  totalAmount: number;
  itemsCount: number;
}

const CartContext = createContext<CartContextData | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { success, error: showError } = useNotification();

  // Load cart from database or sessionStorage
  const loadCart = useCallback(async () => {
    try {
      setLoading(true);

      if (user) {
        // Load from database for logged-in users
        const { data: abandonedCart, error } = await supabase
          .from('abandoned_carts')
          .select('cart_data')
          .eq('user_id', user.id)
          .single();

        if (!error && abandonedCart?.cart_data) {
          setCart(abandonedCart.cart_data);
          return;
        }
      }

      // Fallback to sessionStorage
      const savedCart = sessionStorage.getItem('doce-encanto-cart');
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Save cart to database or sessionStorage
  const saveCart = useCallback(async (newCart: CartItem[]) => {
    try {
      if (user) {
        // Save to database for logged-in users
        const totalAmount = newCart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

        await supabase
          .from('abandoned_carts')
          .upsert({
            user_id: user.id,
            cart_data: newCart,
            total_amount: totalAmount,
            updated_at: new Date().toISOString(),
          }, { onConflict: 'user_id' });
      } else {
        // Save to sessionStorage for non-logged users
        sessionStorage.setItem('doce-encanto-cart', JSON.stringify(newCart));
      }
    } catch (error) {
      console.error('Error saving cart:', error);
    }
  }, [user]);

  // Load cart on mount and when user changes
  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const addItem = useCallback(async (product: Omit<CartItem, 'quantity'>) => {
    try {
      const existingItem = cart.find(item => item.id === product.id);
      let newCart: CartItem[];

      if (existingItem) {
        newCart = cart.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        newCart = [...cart, { ...product, quantity: 1 }];
      }

      setCart(newCart);
      await saveCart(newCart);
      success('Produto adicionado ao carrinho');
    } catch (error) {
      console.error('Error adding item to cart:', error);
      showError('Erro ao adicionar produto ao carrinho');
    }
  }, [cart, saveCart, success, showError]);

  const removeItem = useCallback(async (productId: string) => {
    try {
      const newCart = cart.filter(item => item.id !== productId);
      setCart(newCart);
      await saveCart(newCart);
      success('Produto removido do carrinho');
    } catch (error) {
      console.error('Error removing item from cart:', error);
      showError('Erro ao remover produto do carrinho');
    }
  }, [cart, saveCart, success, showError]);

  const updateQuantity = useCallback(async (productId: string, quantity: number) => {
    try {
      if (quantity <= 0) {
        await removeItem(productId);
        return;
      }

      const newCart = cart.map(item =>
        item.id === productId ? { ...item, quantity } : item
      );

      setCart(newCart);
      await saveCart(newCart);
    } catch (error) {
      console.error('Error updating quantity:', error);
      showError('Erro ao atualizar quantidade');
    }
  }, [cart, saveCart, removeItem, showError]);

  const clearCart = useCallback(async () => {
    try {
      setCart([]);
      await saveCart([]);

      if (user) {
        // Also delete from database
        await supabase
          .from('abandoned_carts')
          .delete()
          .eq('user_id', user.id);
      } else {
        sessionStorage.removeItem('doce-encanto-cart');
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  }, [user, saveCart]);

  const totalAmount = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const itemsCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        totalAmount,
        itemsCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}

